import React, { useEffect, useMemo, useState } from "react"

import { FeatureFlagsExistenceLogicalOperator } from "@common-shared/config/featureFlags.config.ts"
import { AvailableLookupConsumer } from "@common-shared/config/lookup.config.ts"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"

import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { NirvanaBreathingLoaderElement } from "@front-shared/elements/nirvanaBreathingLoader.element.tsx"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"
import { useCptCodes } from "@front-shared/hooks/cptCodes.hook.tsx"
import { useIsMobile } from "@front-shared/hooks/isMobile.hook.ts"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import {
    CoverageCheckerMode,
    CoverageCheckerRunningState,
} from "../../config/checker.config"
import { FEATURE_FLAGS_CONFIG } from "../../config/featureFlags.config"
import { GENERAL_CONFIG } from "../../config/general.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { CheckerHelper } from "../../helpers/checker.helper"
import { PermissionsHelper } from "../../helpers/permissions.helper.ts"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { checkerSelector } from "../../store/selectors/checker.selector"
import { profileSelector } from "../../store/selectors/profile.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { userDetailsSelector } from "../../store/selectors/userDetails.selector"
import {
    checkerReset,
    checkerResetWithKeepingInputData,
} from "../../store/slices/checker.slice"
import { coveragePayersGetList } from "../../store/thunks/coveragePayers.thunks"
import { OrganizationQuoteComponent } from "../general/quotaChecksProgressComponent.component"
import { CoverageCheckerCSVComponent } from "./csv/coverageCheckerCSV.component"
import { CoverageCheckerManualComponent } from "./manual/coverageCheckerManual.component"
import { ResultsDownloadComponent } from "./resultsDownload.component"
import { RunningTypeSelectorComponent } from "./runningTypeSelector.component"

import refreshIcon from "@front-shared/assets/images/icons/refresh-dark2.svg"

export const CoverageCheckerComponent: React.FunctionComponent = () => {
    useScrollFix()

    const dispatch = useAppDispatch()

    const [manualCheckerKey, setManualCheckerKey] = useState(
        new Date().getTime(),
    )

    const isMobile = useIsMobile()

    const profile = useAppSelector(profileSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { selectedPracticeUsageQuota } = useAppSelector(userDetailsSelector)
    const checkerState = useAppSelector(checkerSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    useIsFeatureFlagEnabled(
        FEATURE_FLAGS_CONFIG.checkerFeatureFlags,
        FeatureFlagsExistenceLogicalOperator.OR,
        ROUTES_CONFIG.profileUrl,
    )

    const { availableCptCodes } = useCptCodes(
        AvailableLookupConsumer.coveragePortal,
        selectedPracticeRole?.availableModalities,
    )

    const canRunCoverageChecksByPermissions = useMemo<boolean>(
        () =>
            PermissionsHelper.hasPermission(
                [
                    OneVerifyPermission.runCoverageChecks,
                    OneVerifyPermission.runCsvBulkCoverageChecks,
                ],
                selectedPracticeRole,
            ),
        [selectedPracticeRole],
    )

    // If received more than X results -> stop loader, so user can preview results already
    // Also show spinner if processing CSV right now
    const isLoading = useMemo<boolean>(() => {
        const continuousMonitoringResultsCount =
            checkerState.continuousMonitoringCoverageChecks.filter(
                item => !!item.result,
            ).length

        const medicaidResultsCount = checkerState.medicaidChecks.filter(
            item => !!item.result,
        ).length

        const isBulkChecksResultsPending =
            continuousMonitoringResultsCount <= 5 && medicaidResultsCount <= 5

        return (
            checkerState.runningState ===
                CoverageCheckerRunningState.PROCESS_CSV ||
            (RuntimeHelper.isBulkCoverageChecksLoading() &&
                isBulkChecksResultsPending)
        )
    }, [
        runtimeState.isLoading,
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.runningState,
    ])

    // Listen for browser back btn -> reset Coverage Checker page
    useEffect(() => {
        window.addEventListener("popstate", onReset)

        return () => {
            window.removeEventListener("popstate", onReset)
        }
    }, [])

    // Load fresh coverage payers when selectedPracticeRole changes
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            coveragePayersGetList({
                payload: undefined,
                practice: selectedPracticeRole.practice,
            }),
        )
    }, [selectedPracticeRole?.id])

    const onReset = () => {
        dispatch(checkerReset())
    }

    const onResetWithKeepingInputData = () => {
        dispatch(checkerResetWithKeepingInputData())
    }

    const onResetManualChecker = () => {
        setManualCheckerKey(new Date().getTime())

        dispatch(checkerReset())
    }

    const isResultsView = useMemo<boolean>(() => {
        return [
            CoverageCheckerRunningState.RESULTS_MANUAL,
            CoverageCheckerRunningState.RESULTS_CSV,
        ].includes(checkerState.runningState)
    }, [checkerState.runningState])

    const headerTitle = useMemo<string>(() => {
        switch (checkerState.runningState) {
            case CoverageCheckerRunningState.PREVIEW_CSV:
                return "Preview Checks"

            default:
                return "Nirvana Coverage Checker"
        }
    }, [checkerState.runningState])

    const headerSubtitle = useMemo<JSX.Element | null>(() => {
        switch (checkerState.runningState) {
            case CoverageCheckerRunningState.PREVIEW_CSV:
                return (
                    <>
                        You can remove records before you run the checks.
                        <br className="md:hidden" /> Note that{" "}
                        <span className="text-bold">
                            any checks with errors will not be included
                        </span>{" "}
                        in the run.
                    </>
                )

            default:
                return null
        }
    }, [checkerState.runningState])

    // For csv checker during preview show how many quota will be used
    const csvInProgressQuotaUsage = useMemo<number>(() => {
        if (
            checkerState.runningState !==
            CoverageCheckerRunningState.PREVIEW_CSV
        ) {
            return 0
        }

        return CheckerHelper.getInProgressChecksQuotaAmount(
            checkerState.csvCheckType,
            checkerState.continuousMonitoringCoverageChecks,
            checkerState.medicaidChecks,
            selectedPracticeUsageQuota,
        )
    }, [
        selectedPracticeUsageQuota,
        checkerState.runningState,
        checkerState.csvCheckType,
        checkerState.continuousMonitoringCoverageChecks,
        checkerState.medicaidChecks,
    ])

    return runtimeState.enabledFeatureFlags ? (
        <div className="pb-32 relative">
            <PageHelmetElement
                title="Coverage Checker"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <NirvanaBreathingLoaderElement isActive={isLoading} />

            {!profile || !availableCptCodes.length ? (
                <SpinnerElement containerClassName="text-center mt-[48px]" />
            ) : canRunCoverageChecksByPermissions ? (
                <div>
                    <div className="max-w-[760px]">
                        <PrimaryText
                            className={isMobile ? "mt-[72px]" : ""}
                            typography={isMobile ? "h2" : "h1"}
                        >
                            {headerTitle}
                        </PrimaryText>

                        {headerSubtitle ? (
                            <PrimaryText weight="medium" className="mt-[8px]">
                                {headerSubtitle}
                            </PrimaryText>
                        ) : null}

                        <OrganizationQuoteComponent
                            className="mt-[16px]"
                            inProgressQuotaUsage={csvInProgressQuotaUsage}
                            collapsible={!csvInProgressQuotaUsage}
                        />

                        {checkerState.runningState !==
                        CoverageCheckerRunningState.PREVIEW_CSV ? (
                            <div className="flex items-center mt-[24px]">
                                <RunningTypeSelectorComponent className="flex-1" />

                                {isResultsView ? (
                                    <ButtonElement
                                        label="Run another check(s)"
                                        type="default"
                                        size="large"
                                        icon={refreshIcon}
                                        htmlType="button"
                                        onClick={onResetWithKeepingInputData}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>

                    {!isLoading && <ResultsDownloadComponent />}

                    {checkerState.checkerMode === CoverageCheckerMode.CSV ? (
                        <CoverageCheckerCSVComponent isLoading={isLoading} />
                    ) : (
                        <CoverageCheckerManualComponent
                            key={manualCheckerKey}
                            resetForm={onResetManualChecker}
                        />
                    )}
                </div>
            ) : null}
        </div>
    ) : (
        <SpinnerElement containerClassName="text-center mt-[48px]" />
    )
}
